import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { blogsMetadata as fallbackBlogs } from '../data/blogsMetadata';
import { CONFIG } from '../utils/config';
import { parseFrontmatter } from '../pages/Blog';

const HomeBlogSection = () => {
    const { t } = useTranslation();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await axios.get(`${CONFIG.API_BASE}/blogs`);
                if (res.data && res.data.length > 0) {
                    const parsedBlogs = res.data.map(b => {
                        const { data } = parseFrontmatter(b.content || "");
                        return {
                            id: b.id,
                            title: data.title || b.title,
                            slug: data.slug || b.id,
                            category: data.category || b.category || "Update",
                            readTime: data.read_time || "3 min",
                            image: data.cover_image || "/assets/blog/default_cover.webp",
                            excerpt: data.excerpt || b.content?.substring(0, 100) + '...',
                            author: data.author || b.author || "Doumene M. Rosvel",
                            date: data.date || b.created_at
                        };
                    });
                    // Only take the newest 3
                    setBlogs(parsedBlogs.slice(0, 3));
                } else {
                    setBlogs(fallbackBlogs.slice(0, 3));
                }
            } catch(e) {
                console.error("Failed to fetch blogs", e);
                setBlogs(fallbackBlogs.slice(0, 3));
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    return (
        <section id="latest-news" className="relative py-24 border-y border-glass-border overflow-hidden">
            <div className="bg-blob" style={{ top: '50%', right: '10%' }}></div>
            <div className="container relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-black mb-4 uppercase tracking-tighter text-text-main" data-aos="fade-right">
                            {t('homeBlogSection.title1')} <span className="text-primary italic">{t('homeBlogSection.title2')}</span>
                        </h2>
                        <p className="text-text-muted text-lg max-w-2xl" data-aos="fade-right" data-aos-delay="100">
                            {t('homeBlogSection.subtitle')}
                        </p>
                    </div>
                    <Link to="/blog" className="px-8 py-3 glass hover:bg-primary/10 border-primary/20 text-primary font-bold rounded-xl transition-all hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(0,255,136,0.2)] text-sm uppercase tracking-widest whitespace-nowrap" data-aos="fade-left">
                        {t('homeBlogSection.viewAll')}
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {loading ? (
                        [...Array(3)].map((_, i) => (
                            <div key={i} className="glass p-6 h-80 rounded-2xl animate-pulse bg-white/5 border border-white/10"></div>
                        ))
                    ) : (
                        blogs.map((blog, idx) => (
                            <Link 
                                to={`/blog/${blog.slug}`} 
                                key={blog.id}
                                className="glass p-6 group hover:border-primary/50 transition-all duration-300 relative overflow-hidden flex flex-col h-full rounded-2xl bg-black/20"
                                data-aos="fade-up"
                                data-aos-delay={idx * 150}
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-primary/20 transition-colors"></div>
                                
                                <div className="relative h-48 w-full overflow-hidden rounded-xl mb-6 border border-white/5 z-10 bg-black/40">
                                    <img 
                                        src={blog.image} 
                                        alt={blog.title} 
                                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                        loading="lazy"
                                    />
                                </div>

                                <div className="flex justify-between items-center mb-4 text-xs font-mono text-primary z-10">
                                    <span className="uppercase tracking-widest font-black">{blog.category}</span>
                                    <span>{blog.readTime}</span>
                                </div>
                                
                                <h3 className="text-xl font-bold mb-4 text-text-main group-hover:text-primary transition-colors z-10 leading-tight">
                                    {blog.title}
                                </h3>
                                
                                <p className="text-text-muted mb-8 flex-grow z-10 text-sm leading-relaxed line-clamp-3">
                                    {blog.excerpt}
                                </p>
                                
                                <div className="flex items-center justify-between mt-auto z-10 border-t border-white/10 pt-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-primary to-secondary flex items-center justify-center text-black font-bold text-xs">
                                            DR
                                        </div>
                                        <div className="text-sm text-text-muted">
                                            {blog.author}
                                            <div className="text-xs opacity-70">{new Date(blog.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                        </div>
                                    </div>
                                    
                                    <span className="text-primary font-bold group-hover:translate-x-2 transition-transform">
                                        {t('homeBlogSection.readBtn')}
                                    </span>
                                </div>
                            </Link>
                        ))
                    )}
                </div>
                
                <div className="mt-16 text-center" data-aos="fade-up" data-aos-delay="300">
                     <button 
                        onClick={() => window.scrollTo({ top: document.getElementById('calculator')?.offsetTop - 100, behavior: 'smooth' })}
                        className="px-12 py-5 border-2 border-[#00ff88] text-[#00ff88] font-black text-sm uppercase tracking-[0.2em] rounded-xl hover:bg-[#00ff88] hover:text-black transition-all active:scale-95 shadow-[0_0_15px_rgba(0,255,136,0.2)]"
                    >
                        {t('homeBlogSection.cta')}
                    </button>
                </div>
            </div>
        </section>
    );
};

export default HomeBlogSection;
